import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {
  private db: IDBDatabase | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.initDB();
    }
  }

  private initDB() {
    const request = indexedDB.open('BlobStore', 1);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('blobs')) {
        const store = db.createObjectStore('blobs', { keyPath: 'id', autoIncrement: true });
        store.createIndex('jobId', 'jobId', { unique: false });
      }
    };

    request.onsuccess = () => {
      this.db = request.result;
    };

    request.onerror = (event) => {
      console.error('IndexedDB error:', (event.target as IDBRequest).error);
    };
  }

  public async storeBlob(blob: Blob, jobId: string): Promise<number | undefined> {
    if (!this.db) return;

    return new Promise<number | undefined>((resolve, reject) => {
      const transaction = this.db!.transaction(['blobs'], 'readwrite');
      const store = transaction.objectStore('blobs');
      const request = store.add({ blob, jobId });

      request.onsuccess = () => {
        resolve(request.result as number);
      };

      request.onerror = (event) => {
        console.error('IndexedDB error:', (event.target as IDBRequest).error);
        reject(undefined);
      };
    });
  }

  public async getBlobsByJobId(jobId: string): Promise<Blob[]> {
    if (!this.db) return [];

    return new Promise<Blob[]>((resolve, reject) => {
      const transaction = this.db!.transaction(['blobs'], 'readonly');
      const store = transaction.objectStore('blobs');
      const index = store.index('jobId');
      const request = index.getAll(IDBKeyRange.only(jobId));

      request.onsuccess = () => {
        const blobs = request.result.map(record => record.blob);
        resolve(blobs);
      };

      request.onerror = (event) => {
        console.error('IndexedDB error:', (event.target as IDBRequest).error);
        reject([]);
      };
    });
  }

  public async deleteBlobsByJobId(jobId: string): Promise<void> {
    if (!this.db || !jobId) return;

    return new Promise<void>((resolve, reject) => {
      try {
        console.log(`Deleting blobs with jobId: ${jobId}...`);
        const transaction = this.db!.transaction(['blobs'], 'readwrite');
        const store = transaction.objectStore('blobs');
        const index = store.index('jobId');
        const request = index.openCursor(IDBKeyRange.only(jobId));

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            console.log(`Deleted blobs with jobId: ${jobId}`);
            resolve();
          }
        };

        request.onerror = (event) => {
          console.error('IndexedDB error:', (event.target as IDBRequest).error);
          reject((event.target as IDBRequest).error);
        };
      } catch (error) {
        console.error('Error in deleteBlobsByJobId:', error);
        reject(error);
      }
    });
  }
}
